/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { CodeSnippet, InlineNotification } from '@carbon/react';
import { Code } from '@carbon/react/icons';
import urlJoin from 'proper-url-join';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  selectPlan,
  selectResourceGroupIdForPremiumPlan
} from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { getTenant } from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import {
  isAws,
  isCpd,
  deployEnv,
  endpoints,
  wxoUrl
} from '@ibm-watson/shared/modules/ia/util/config';
import {
  cpdIdFromCrn,
  parse as parseCrn
} from '@ibm-watson/shared/modules/ia/util/crn';
import localStorage from '@ibm-watson/shared/modules/ia/util/localStorage';
import { COPY_EMBED_LINK } from '@ibm-watson/shared/modules/ia/util/task-list/constants';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppSelector } from '@ibm-watson/shared/store';
import { isWxoEnabled } from '../../../../ia/util/config';
import {
  buildIntegrationsUrlCpd,
  getRegionValue,
  getRegionValueForEnv
} from '../../../util/urls';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { links } from '../links';
import { EmbedTab as messages } from '../messages';

interface EmbedProps {
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  integrationId: string;
}
export const Embed = ({ integrationId }: EmbedProps) => {
  const { formatMessage: t } = useIntl();

  const plan = useAppSelector(selectPlan);
  const premiumPlanIntegrationOrgId = useAppSelector(
    selectResourceGroupIdForPremiumPlan
  );
  const { region, instance: tenant } = useAppSelector(getTenant);
  const { service_instance: serviceInstanceID } = parseCrn(tenant);
  const regionValue = isWxoEnabled
    ? getRegionValueForEnv(region, deployEnv)
    : getRegionValue(region);

  const [snippet, setSnippet] = useState<string>('');

  useEffect(() => {
    let sourceUrl = endpoints.webChat;
    let script = ``;
    let cloudPrivateHostURL = '';
    let orchestratePrivateHostURL;

    if (isCpd) {
      cloudPrivateHostURL = buildIntegrationsUrlCpd();
      //append the appropriate folder structure for cpd
      sourceUrl = urlJoin(cloudPrivateHostURL, 'static', 'webchat');
    }

    // If wxo is enabled and we are either on CPD or we are on AWS CIO/Premium-like,
    // we need to tell the WXO forms where to POST to.
    if (isWxoEnabled && (isCpd || (isAws && premiumPlanIntegrationOrgId))) {
      orchestratePrivateHostURL = wxoUrl;
    }

    //since this url is used to load web chat end the url with WatsonAssistantChatEntry.js file
    sourceUrl =
      urlJoin(sourceUrl, 'versions') +
      `/" + (window.watsonAssistantChatOptions.clientVersion || 'latest') + "/WatsonAssistantChatEntry.js`;

    // OK GET READY FOR SOME FUN.
    // Here we outline the if/else statements to get all the needed data for the embed snippets for our
    // varied environments. Of note the following environments are not explictly covered and hit the fallback:
    // wxa on Cloud (this is on purpose), wxo on Cloud, wxo on Cloud "premium".

    // This is regular wxa on "premium" plan cloud.
    if (premiumPlanIntegrationOrgId && !orchestratePrivateHostURL) {
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
    subscriptionID: "${premiumPlanIntegrationOrgId}", // Your plan subscription ID.
    onLoad: async (instance) => { await instance.render(); }
  };`;
      // This is "premium" with orchestrate on AWS, e.g. CIO AWS.
      // WXO hard codes cloud AWS production urls into their form MFE, so orchestratePrivateHostURL is required here to hand to them.
    } else if (isWxoEnabled && isAws && premiumPlanIntegrationOrgId) {
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
    subscriptionID: "${premiumPlanIntegrationOrgId}", // Your plan subscription ID.
    orchestratePrivateHostURL: "${orchestratePrivateHostURL}", // The fully-qualified URL for the corresponding watsonx Orchestrate server
    orchestrateUIAgentExtensions: false, // If you wish to enable optional UI Agent extensions.
    onLoad: async (instance) => { await instance.render(); }
  };`;
      // CP4D wxo. Needs cloudPrivateHostURL just like wxa cp4d AND requires the url for the form MFE to wxo server.
    } else if (isWxoEnabled && isCpd) {
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${cpdIdFromCrn(
      serviceInstanceID
    )}", // The ID of your service instance.
    cloudPrivateHostURL: "${cloudPrivateHostURL}", // The integration service URL for your provisioned instance.
    orchestratePrivateHostURL: "${orchestratePrivateHostURL}", // The fully-qualified URL for the corresponding watsonx Orchestrate server
    orchestrateUIAgentExtensions: false, // If you wish to enable optional UI Agent extensions.
    onLoad: async (instance) => { await instance.render(); }
  };`;
      // non-premium on wxo on IBM Cloud.
    } else if (isWxoEnabled && !isAws) {
      script = `window.watsonAssistantChatOptions = {
  integrationID: "${integrationId}", // The ID of this integration.
  region: "${regionValue}", // The region your integration is hosted in.
  serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
  orchestrateUIAgentExtensions: false, // If you wish to enable optional UI Agent extensions.
  onLoad: async (instance) => { await instance.render(); }
};`;
      // CP4D wxa. Needs cloudPrivateHostURL to know where to send messages to.
    } else if (orchestratePrivateHostURL) {
      script = `window.watsonAssistantChatOptions = {
        integrationID: "${integrationId}", // The ID of this integration.
        region: "${regionValue}", // The region your integration is hosted in.
        serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
        orchestratePrivateHostURL: "${orchestratePrivateHostURL}", // The fully-qualified URL for the corresponding watsonx Orchestrate server
        orchestrateUIAgentExtensions: false, // If you wish to enable optional UI Agent extensions.
        onLoad: async (instance) => { await instance.render(); }
  };`;
      // CP4D wxa without Orchestrate.
    } else if (isCpd) {
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${cpdIdFromCrn(
      serviceInstanceID
    )}", // The ID of your service instance.
    cloudPrivateHostURL: "${cloudPrivateHostURL}", // The integration service URL for your provisioned instance.
    onLoad: async (instance) => { await instance.render(); }
  };`;
      // Regular wxo on Aws.
    } else if (isWxoEnabled && isAws) {
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
    orchestrateUIAgentExtensions: false, // If you wish to enable optional UI Agent extensions.
    onLoad: async (instance) => { await instance.render(); }
  };`;
    } else {
      // Regular old lite/plus IBM Cloud plans.
      script = `window.watsonAssistantChatOptions = {
    integrationID: "${integrationId}", // The ID of this integration.
    region: "${regionValue}", // The region your integration is hosted in.
    serviceInstanceID: "${serviceInstanceID}", // The ID of your service instance.
    onLoad: async (instance) => { await instance.render(); }
  };`;
    }
    setSnippet(`<script>
  ${script}
  setTimeout(function(){
    const t=document.createElement('script');
    t.src="${sourceUrl}";
    document.head.appendChild(t);
  });
</script>`);
  }, [
    integrationId,
    plan,
    premiumPlanIntegrationOrgId,
    regionValue,
    serviceInstanceID
  ]);

  const handleEmbedCopyClick = snippet => {
    copyContentToClipboard(snippet);
    // To determine content on Preview page's web chat tile
    localStorage.set(COPY_EMBED_LINK, {
      region,
      plan,
      serviceInstanceID
    });
    ctaEvents.webChatEmbedLinkCopied();
  };
  return (
    <TabContent
      ImageComponent={() => <Code size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={t(messages.subtitle)}
      learnMoreLink={links.EMBED_LINK}>
      <div className="TabContent__code-snippet">
        {integrationId && regionValue ? (
          <CodeSnippet
            feedback={t(sharedMessages.copied)}
            copyButtonDescription={t(sharedMessages.copyToClipboard)}
            copyText={t(sharedMessages.copyToClipboard)}
            onClick={() => handleEmbedCopyClick(snippet)}
            type="multi">
            {snippet}
          </CodeSnippet>
        ) : (
          <InlineNotification
            hideCloseButton
            kind="error"
            role="alert"
            subtitle={t(messages.codeSnippetErrorText)}
            title={t(messages.codeSnippetErrorTitle)}
            statusIconDescription={t(messages.codeSnippetErrorTitle)}
          />
        )}
      </div>
    </TabContent>
  );
};
